import React from 'react';
import PageLayout from '../components/PageLayout';
import { Card } from '../components/ui/card';
import SEOHead from '../components/SEOHead';
import { Cookie, Settings, BarChart3, Shield } from 'lucide-react';

const COOKIE_TYPES = [
  {
    name: 'Essential Cookies',
    icon: Shield,
    required: true,
    color: '#10B981',
    desc: 'These cookies are necessary for the website to function and cannot be switched off. They are set in response to actions you make such as setting your privacy preferences, logging in, or filling in forms.',
    examples: ['session_token — keeps you logged in', 'csrf_token — protects against cross-site request forgery', 'cookie_consent — remembers your cookie preferences'],
  },
  {
    name: 'Functional Cookies',
    icon: Settings,
    required: false,
    color: '#1E3A8A',
    desc: 'These cookies enhance functionality and personalisation. They may be set by us or by third-party providers whose services we have added to our pages.',
    examples: ['theme_preference — remembers dark/light mode', 'language — remembers your preferred language', 'airport_feed_filter — remembers your last selected airport'],
  },
  {
    name: 'Analytics Cookies',
    icon: BarChart3,
    required: false,
    color: '#FF4500',
    desc: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. All information these cookies collect is aggregated and therefore anonymous.',
    examples: ['_ga — Google Analytics page view tracking', '_gid — Google Analytics session distinction', 'plausible_ignore — opt-out of Plausible analytics'],
  },
];

export default function CookiePolicy() {
  return (
    <PageLayout>
      <SEOHead
        title="Cookie Policy — Fly N Out"
        description="Learn how Fly N Out uses cookies and similar technologies to improve your browsing experience. Manage your preferences and understand your privacy choices."
        url="/cookies"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Cookie className="w-7 h-7 text-[#FF4500]" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Cookie Policy</h1>
          </div>
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          <div className="mt-4 p-4 rounded-xl bg-[#FF4500]/8 border border-[#FF4500]/20">
            <p className="text-sm leading-relaxed">
              Fly N Out uses cookies and similar tracking technologies to enhance your experience. This policy explains what cookies we use and why.
            </p>
          </div>
        </div>

        <Card className="fly-card p-5 mb-6">
          <h2 className="font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>What are Cookies?</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences, keep you logged in, and understand how you use the site. Session cookies are deleted when you close your browser, while persistent cookies remain until they expire or you delete them.
          </p>
        </Card>

        {COOKIE_TYPES.map(({ name, icon: Icon, required, color, desc, examples }) => (
          <Card key={name} className="fly-card p-5 mb-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{name}</h3>
                  {required ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">Always Active</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Optional</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{desc}</p>
                <div className="space-y-1">
                  {examples.map(ex => (
                    <div key={ex} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                      <code className="font-mono">{ex}</code>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        ))}

        <Card className="fly-card p-5 mb-4">
          <h2 className="font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Managing Cookies</h2>
          <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
            <p>You can control cookies through your browser settings. Most browsers allow you to refuse cookies or delete existing ones. However, disabling essential cookies may prevent you from logging in or using core features.</p>
            <p>To clear cookies in your browser: Settings → Privacy & Security → Clear browsing data → Cookies and site data.</p>
            <p>For more information about cookies and how to manage them, visit <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">allaboutcookies.org</a>.</p>
          </div>
        </Card>

        <div className="text-center mt-6">
          <p className="text-xs text-muted-foreground">
            Questions about our Cookie Policy? <a href="/contact" className="text-[#FF4500] hover:underline">Contact us</a>
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
